import React, { useState } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  fabContainer: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '12px',
  },
  suggestionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-end',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  },
  suggestionPill: {
    backgroundColor: '#ffffff',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '20px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: tokens.shadow4,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  fabWrapper: {
    position: 'relative',
    width: '44px',
    height: '44px',
  },
  fabShadow: {
    position: 'absolute',
    inset: '0',
    borderRadius: '9999px',
    pointerEvents: 'none',
    // Shadow 12 Ambient - blur 1px, opacity 0.06
    boxShadow: `
      0px 0px 1px 0px rgba(0, 0, 0, 0.06),
      0px 6.55px 6px -4.36px rgba(0, 0, 0, 0.07)
    `,
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '0',
      borderRadius: '9999px',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.48) 0%, rgba(0, 0, 0, 0.12) 100%)',
      filter: 'blur(1px)',
      opacity: 0.48,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '6.55px',
      left: '4.36px',
      right: '-4.36px',
      bottom: '-6.55px',
      borderRadius: '9999px',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.35) 0%, rgba(0, 0, 0, 0.15) 100%)',
      filter: 'blur(6px)',
      opacity: 0.35,
    },
  },
  fab: {
    position: 'relative',
    width: '44px',
    height: '44px',
    borderRadius: '9999px',
    backgroundColor: '#ffffff',
    border: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    zIndex: 2,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:active': {
      backgroundColor: '#ebebeb',
    },
  },
  copilotIcon: {
    width: '24px',
    height: '24px',
  },
});

interface CopilotFABProps {
  onClick: () => void;
  onSuggestionClick?: (suggestion: string) => void;
}

const CopilotFAB: React.FC<CopilotFABProps> = ({ onClick, onSuggestionClick }) => {
  const styles = useStyles();
  const [isHovered, setIsHovered] = useState(false);

  const suggestions = [
    "Get key insights",
    "Send reminders"
  ];

  const handleSuggestionClick = (suggestion: string) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    }
    onClick();
  };

  return (
    <div 
      className={styles.fabContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Suggestion Pills */}
      <div 
        className={styles.suggestionsContainer}
        style={{
          opacity: isHovered ? 1 : 0,
          transform: isHovered ? 'translateY(0)' : 'translateY(10px)',
          pointerEvents: isHovered ? 'auto' : 'none',
          visibility: isHovered ? 'visible' : 'hidden',
        }}
      >
        {suggestions.map((suggestion, index) => (
          <div
            key={index}
            className={styles.suggestionPill}
            onClick={() => handleSuggestionClick(suggestion)}
          >
            {suggestion}
          </div>
        ))}
      </div>

      {/* FAB Button */}
      <div className={styles.fabWrapper}>
        <div className={styles.fabShadow} />
        <button 
          className={styles.fab}
          onClick={onClick}
          aria-label="Open Copilot"
          type="button"
        >
          <img 
            src="/src/assets/office/ribbon/Copilot.svg" 
            alt="Copilot" 
            className={styles.copilotIcon}
          />
        </button>
      </div>
    </div>
  );
};

export default CopilotFAB;
